import React, { useState, useEffect } from 'react';

// Indianapolis area ZIP code centroids (lat/lng)
const ZIP_CENTROIDS = {
  '46236': { lat: 39.9087, lng: -85.9825 },  // Lawrence/Geist
  '46240': { lat: 39.9068, lng: -86.1189 },  // Nora
  '46220': { lat: 39.8684, lng: -86.0895 },  // Broad Ripple
  '46256': { lat: 39.9186, lng: -86.0125 },  // Castleton
  '46250': { lat: 39.8981, lng: -86.0432 },  // Castleton area
  '46268': { lat: 39.8778, lng: -86.2056 },  // Pike Township
  '46260': { lat: 39.9037, lng: -86.1583 },  // Nora/Carmel
  '46280': { lat: 39.9431, lng: -86.1367 },  // Carmel
  '46290': { lat: 39.9667, lng: -86.1167 },  // Carmel north
  '46032': { lat: 39.9784, lng: -86.1297 },  // Carmel
  '46033': { lat: 39.9956, lng: -86.0867 },  // Carmel east
  '46074': { lat: 40.0456, lng: -86.0256 },  // Westfield
  '46077': { lat: 39.9567, lng: -86.2567 },  // Zionsville
  '46060': { lat: 40.0456, lng: -85.9567 },  // Noblesville
  '46062': { lat: 40.0789, lng: -86.0123 },  // Noblesville
  '46038': { lat: 39.9567, lng: -85.9567 },  // Fishers
  '46037': { lat: 39.9234, lng: -85.9234 },  // Fishers east
  '46040': { lat: 39.9123, lng: -85.8234 },  // Fortville
  '46234': { lat: 39.8067, lng: -86.2967 },  // Speedway/west
  '46224': { lat: 39.7867, lng: -86.2367 },  // Speedway
  '46214': { lat: 39.7756, lng: -86.2756 },  // West Indy
  '46222': { lat: 39.7923, lng: -86.1923 },  // Near westside
  '46208': { lat: 39.8234, lng: -86.1634 },  // Butler-Tarkington
  '46205': { lat: 39.8367, lng: -86.1267 },  // Meridian-Kessler
  '46202': { lat: 39.7767, lng: -86.1467 },  // Downtown
  '46204': { lat: 39.7689, lng: -86.1567 },  // Downtown
  '46201': { lat: 39.7734, lng: -86.1034 },  // Near eastside
  '46203': { lat: 39.7367, lng: -86.1167 },  // Fountain Square
  '46219': { lat: 39.7734, lng: -86.0234 },  // Irvington
  '46229': { lat: 39.7934, lng: -85.9634 },  // Warren Township
  '46235': { lat: 39.8234, lng: -85.9234 },  // East Indy
  '46226': { lat: 39.8434, lng: -86.0434 },  // Lawrence
  '46218': { lat: 39.8234, lng: -86.1134 },  // Mapleton-Fall Creek
  '46228': { lat: 39.8567, lng: -86.1867 },  // Eagledale
  '46254': { lat: 39.8534, lng: -86.2534 },  // Northwest
  '46278': { lat: 39.8867, lng: -86.2867 },  // Northwest
  '46112': { lat: 39.7634, lng: -86.3934 },  // Brownsburg
  '46143': { lat: 39.5812, lng: -86.1055 },  // Greenwood
  '46142': { lat: 39.6134, lng: -86.1334 },  // Greenwood
  '46227': { lat: 39.6834, lng: -86.1334 },  // Southport
  '46217': { lat: 39.6734, lng: -86.1734 },  // South Indy
  '46237': { lat: 39.6934, lng: -86.0634 },  // Beech Grove
  '46107': { lat: 39.7167, lng: -86.0867 },  // Beech Grove
  '46239': { lat: 39.7434, lng: -85.9934 },  // Southeast
  '46259': { lat: 39.6734, lng: -85.9834 },  // Franklin Township
  '46113': { lat: 39.6134, lng: -86.2834 },  // Camby
  '46221': { lat: 39.7234, lng: -86.2134 },  // Airport area
  '46241': { lat: 39.7534, lng: -86.2534 },  // West Indy
  '46231': { lat: 39.7134, lng: -86.3134 },  // West Indy
};

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
}

function HotspotPanel({ onSelectHotspot, onLoadAddresses, isVisible, onClose }) {
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZip, setSelectedZip] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  useEffect(() => {
    // Load both files and merge storm metadata
    Promise.all([
      fetch('/hotspots_full.json').then(res => res.json()),
      fetch('/hotspots.json').then(res => res.json())
    ])
      .then(([fullData, stormData]) => {
        // Create lookup for storm metadata by ZIP
        const stormLookup = {};
        stormData.forEach(s => {
          stormLookup[s.zip] = s;
        });

        // Merge storm metadata into full data
        const mergedData = fullData.map(h => ({
          ...h,
          stormPriority: stormLookup[h.zip]?.stormPriority || false,
          stormNote: stormLookup[h.zip]?.stormNote || null,
          medianIncome: stormLookup[h.zip]?.medianIncome || null
        }));

        // Sort: storm priority first, then by hot leads count
        mergedData.sort((a, b) => {
          if (a.stormPriority && !b.stormPriority) return -1;
          if (!a.stormPriority && b.stormPriority) return 1;
          return b.hot - a.hot;
        });

        setHotspots(mergedData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load hotspots:', err);
        // Fallback to just full data
        fetch('/hotspots_full.json')
          .then(res => res.json())
          .then(data => {
            setHotspots(data);
            setLoading(false);
          });
      });
  }, []);

  // Generate a deterministic pseudo-random offset from address string
  // This spreads addresses out visually within the ZIP area
  const generateOffsetFromAddress = (address, index) => {
    // Simple hash from address string for consistent positioning
    let hash = 0;
    for (let i = 0; i < address.length; i++) {
      hash = ((hash << 5) - hash) + address.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }

    // Create spread pattern - use index to create grid-like distribution
    const gridSize = Math.ceil(Math.sqrt(index + 1));
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;

    // Spread radius ~0.02 degrees (~2km) with grid offset
    const baseOffset = 0.015;
    const latOffset = (row / gridSize - 0.5) * baseOffset * 2 + (Math.sin(hash) * 0.003);
    const lngOffset = (col / gridSize - 0.5) * baseOffset * 2 + (Math.cos(hash) * 0.003);

    return { latOffset, lngOffset };
  };

  const handleBlast = async (hotspot) => {
    setSelectedZip(hotspot.zip);
    setLoadingAddresses(true);

    // Get ZIP centroid from lookup table
    const centroid = ZIP_CENTROIDS[hotspot.zip] || { lat: 39.7684, lng: -86.1581 }; // Default to Indianapolis center

    // Convert addresses to lead format with approximate coordinates
    const leads = hotspot.addresses.map((addr, i) => {
      // Generate approximate lat/lng based on ZIP centroid + offset
      const offset = generateOffsetFromAddress(addr.a, i);
      const lat = centroid.lat + offset.latOffset;
      const lng = centroid.lng + offset.lngOffset;

      return {
        streetAddress: addr.a,
        city: hotspot.city,
        state: 'IN',
        zip: hotspot.zip,
        ownerName: addr.o,
        assessedValue: addr.v,
        sqft: addr.s,
        ownerOccupied: addr.oo === 1,
        score: addr.sc,
        estRoofSqft: Math.round(addr.s * 1.4),
        estRoofCostLow: Math.round(addr.s * 1.4 * 4),
        estRoofCostHigh: Math.round(addr.s * 1.4 * 8),
        leadQuality: addr.sc >= 70 ? 'hot' : addr.sc >= 50 ? 'warm' : 'cool',
        parcelMatch: true,
        placeId: `hotspot-${hotspot.zip}-${i}`,
        lat,
        lng,
        approximateLocation: true // Flag that coordinates are approximate
      };
    });

    // Sort by score
    leads.sort((a, b) => b.score - a.score);

    setLoadingAddresses(false);
    // Pass centroid with hotspot for map panning
    onLoadAddresses(leads, { ...hotspot, lat: centroid.lat, lng: centroid.lng });
  };

  if (!isVisible) return null;

  return (
    <div className="hotspot-panel">
      <div className="hotspot-header">
        <h3>🔥 Hot Regions</h3>
        <p>Click to blast all addresses in ZIP</p>
        <button className="hotspot-close" onClick={onClose}>×</button>
      </div>

      {loading ? (
        <div className="hotspot-loading">
          <span className="spinner-small"></span>
          Loading hotspots...
        </div>
      ) : (
        <div className="hotspot-list">
          {hotspots.map(h => (
            <div
              key={h.zip}
              className={`hotspot-item ${selectedZip === h.zip ? 'selected' : ''} ${h.stormPriority ? 'storm-priority' : ''}`}
              onClick={() => handleBlast(h)}
            >
              <div className="hotspot-zip-row">
                <span className="hotspot-zip">{h.zip}</span>
                <span className="hotspot-city">{h.city}</span>
                {h.stormPriority && <span className="hotspot-storm-badge">⚡STORM</span>}
              </div>
              <div className="hotspot-stats">
                <span className="hotspot-stat hot">
                  🔥 {h.hot.toLocaleString()} hot
                </span>
                <span className="hotspot-stat occupied">
                  👤 {h.occupied.toLocaleString()} owner
                </span>
                <span className="hotspot-stat total">
                  {h.count.toLocaleString()} total
                </span>
              </div>
              <div className="hotspot-value">
                Avg: {formatCurrency(h.avgValue)}
                {h.medianIncome && <span className="hotspot-income"> • ${(h.medianIncome/1000).toFixed(0)}K income</span>}
              </div>
              {h.stormNote && <div className="hotspot-storm-note">{h.stormNote}</div>}
              {selectedZip === h.zip && loadingAddresses && (
                <div className="hotspot-loading-inline">Loading...</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="hotspot-footer">
        <p>Top 30 Indianapolis ZIP codes by hot leads</p>
      </div>
    </div>
  );
}

export default HotspotPanel;
